import { SendFeedbackDialog } from "@components/header/send-feedback-dialog";
import {
  LanguageCodes,
  HelpCenterMenu,
  HelpCenterMenuLinkNames,
} from "@flat-ui";
import {
  CookieId,
  useIntercom,
  useIsCookieAccepted,
  useIsCookieManagerInitialized,
} from "@faro-lotv/foreign-observers";
import { useToast } from "@hooks/use-toast";
import { Box } from "@mui/material";
import { runtimeConfig } from "@src/runtime-config";
import { useAppSelector } from "@store/store-helper";
import { numUnreadMsgSelector } from "@store/ui/ui-selector";
import { loggedInUserSelector } from "@store/user/user-selector";
import { getMailHref } from "@utils/email-utils";
import {
  AppBarEvents,
  FeedbackModalEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName } from "@utils/user-utils";
import { useState } from "react";
import {
  LATEST_WHATS_NEW_ARTICLE_KEY,
  useCheckWhatsNew,
} from "@hooks/use-check-whats-new";
// If the cookie manager URL is set, we can show the Intercom chat.
// That does not mean that the user gave consent to use it, but we do show the button.
const shouldShowIntercom = !!runtimeConfig.urls.cookieManagerUrl;

export function FaroHelpCenterMenu(): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const numUnreadMsg = useAppSelector(numUnreadMsgSelector);
  const { showToast } = useToast();
  const isCookieAccepted = useIsCookieAccepted();
  const isCookieManagerInitialized = useIsCookieManagerInitialized();
  const [shouldGiveFeedback, setsShouldGiveFeedback] = useState<boolean>(false);
  const intercom = useIntercom();
  const { newArticle, setNewArticle } = useCheckWhatsNew();

  /** Prepare email in case we need to fallback to email support. */
  const supportEmailSubject = encodeURIComponent(
    "Faro Dashboard Support Request - Chat Unavailable"
  );
  const supportEmailBody = encodeURIComponent(
    "Hello Faro Support Team,\n" +
      "I was trying to access the 'chat with us' feature in the Faro Dashboard, " +
      "but the chat system is currently unavailable.\n" +
      "Could you please assist me with this issue?\n\n" +
      "User details:\n" +
      `- Email: ${loggedInUser?.email || "Not available"}\n` +
      `- Timestamp: ${new Date().toISOString()}\n\n` +
      `Best regards,\n${getPrettyName(loggedInUser)}`
  );

  /** Open fallback email window */
  function openSupportEmailFallback(): void {
    window.open(
      getMailHref({
        additionalEmails: [runtimeConfig.mailSupportAddress],
        subject: supportEmailSubject,
        body: supportEmailBody,
      })
    );
  }

  function openFeedbackDialog(): void {
    if (!loggedInUser) {
      showToast({
        message:
          "Unable to open feedback dialog " +
          "Please login to provide feedback.",
        type: "error",
      });
      return;
    }

    setsShouldGiveFeedback(true);
    trackEvent({ name: FeedbackModalEvents.openFeedbackDialog });
  }

  function onOpenChat(): void {
    // Early return for unauthenticated users
    if (!loggedInUser) {
      trackEvent({ name: AppBarEvents.openChatNotLoggedIn });
      showToast({
        message: "Only logged-in users can use the chat.",
        type: "error",
      });
      return;
    }

    // Check if Intercom is properly initialized
    if (!intercom) {
      trackEvent({ name: AppBarEvents.openChatNotInitialized });
      openSupportEmailFallback();
      return;
    }

    // Check cookie consent for Intercom
    if (!isCookieManagerInitialized || !isCookieAccepted(CookieId.intercom)) {
      trackEvent({ name: AppBarEvents.openChatMissingCookie });
      showToast({
        message:
          "You have to enable Intercom in your cookie preferences to use the chat.",
        type: "error",
      });
      return;
    }

    try {
      trackEvent({ name: AppBarEvents.openChatSuccess });
      intercom?.showMessenger();
    } catch {
      // In case something unexpected happens, fallback to email support
      openSupportEmailFallback();
    }
  }

  function onOpenHelpLink(itemName: HelpCenterMenuLinkNames): void {
    trackEvent({
      name: AppBarEvents.openHelpMenuItem,
      props: {
        itemName,
      },
    });

    if (itemName === "whats-new") {
      onOpenWhatsNew();
    }
  }

  /**
   * Handle opening the "What's New" page
   * Sets the latest article in local storage and clears the new article state
   */
  function onOpenWhatsNew(): void {
    if (newArticle) {
      localStorage.setItem(LATEST_WHATS_NEW_ARTICLE_KEY, newArticle);
    }
    setNewArticle(undefined);
  }

  return (
    <>
      <Box component="div">
        <HelpCenterMenu
          helpCenterLanguages={LanguageCodes.English}
          sendFeedback={openFeedbackDialog}
          onOpenHelpCenter={() => {
            trackEvent({ name: AppBarEvents.openHelpMenu });
          }}
          onCloseHelpCenter={() => {
            trackEvent({ name: AppBarEvents.closeHelpMenu });
          }}
          onOpenHelpLink={onOpenHelpLink}
          openChat={shouldShowIntercom ? onOpenChat : undefined}
          numUnreadMsg={numUnreadMsg}
          shouldShowWhatsNewBadge={Boolean(newArticle)}
        />
      </Box>
      {/**
       * TODO: https://faro01.atlassian.net/browse/ST-2140 use the correct language - hash commit where was removed
       * 97fdac6aed9358cfd4efeb06d1c6e7afabe961a8
       */}
      <SendFeedbackDialog
        isDialogOpen={shouldGiveFeedback}
        onClose={() => setsShouldGiveFeedback(false)}
        title="How was your experience with the dashboard?"
        confirmText="Submit"
      />
    </>
  );
}
